import type { SegmentType } from '../@types/CurrentParsingState';

/**
 * Determines the next parsing segment based on current state and input key.
 *
 * This function implements a state machine that parses magnetic stripe card data
 * in ISO 7813 format. The format is: %B[cardNumber]^[name]^[YYMM][serviceCode]...
 *
 * @param str - The accumulated input string so far
 * @param currentSegment - The current parsing state/segment
 * @param key - The key that was just pressed
 * @returns The next segment to transition to, or empty string if invalid
 */
export const detectSegment = (
  str: string,
  currentSegment: SegmentType,
  key: string,
): SegmentType => {
  // Modifier keys don't affect parsing state
  const modifierKeys = ['Shift', 'Alt', 'Control', 'Ctrl', 'Meta'];
  if (modifierKeys.includes(key)) return currentSegment;

  // Helper functions for character type detection
  const isDigit = (char: string): boolean => /^[0-9]$/.test(char);
  const isLetter = (char: string): boolean => /^[A-Za-z]$/.test(char);
  const isNameChar = (char: string): boolean =>
    isLetter(char) || isDigit(char) || char === '.';

  // State machine transitions
  switch (currentSegment) {
    case '':
      // When not in a sequence, detect the start symbol
      if (key === '%') return 'startSymbol';
      return '';

    case 'startSymbol':
      // After '%', we expect 'B' for ISO 7813 format
      if (key === 'B') return 'startChars';
      return '';

    case 'startChars':
      // After '%B', we expect a digit to start the card number
      if (isDigit(key)) return 'cardNum';
      // Allow letters to continue (e.g., if they type '%BG', stay in startChars)
      if (isLetter(key)) return 'startChars';
      return '';

    case 'cardNum':
      // Continue accumulating digits for card number
      if (isDigit(key)) return 'cardNum';
      // '^' marks end of card number
      if (key === '^') return 'cardNumEnd';
      return '';

    case 'cardNumEnd':
      // After card number '^', we expect a character to start the name
      if (isLetter(key)) return 'name';
      return '';

    case 'name':
      // Names can contain letters, digits, periods, slashes, and spaces
      // The parsing logic will handle splitting by slash or space later
      if (isNameChar(key)) return 'name';
      if (key === '/') return 'name'; // Slash is part of name
      if (key === ' ') return 'name'; // Space is part of name
      // '^' marks end of name section
      if (key === '^') return 'nameEnd';
      return '';

    case 'nameEnd':
      // After name section '^', we expect a digit to start expiration year
      if (isDigit(key)) return 'expYear';
      return '';

    case 'expYear': {
      // Extract the date portion after the last '^'
      const lastCaretIndex = str.lastIndexOf('^');
      const datePortion = str.slice(lastCaretIndex + 1);

      if (!isDigit(key)) return '';

      // If we have exactly 2 digits (YY), next digit starts month
      if (datePortion.length === 2) {
        // Validate first digit of month: must be 0 or 1 for valid months (01-12)
        const firstMonthDigit = parseInt(key, 10);
        if (firstMonthDigit > 1) return ''; // Invalid first month digit
        return 'expMonth';
      }

      // Otherwise continue accumulating year digits
      return 'expYear';
    }

    case 'expMonth': {
      // Extract the date portion after the last '^'
      const lastCaretIndex = str.lastIndexOf('^');
      const datePortion = str.slice(lastCaretIndex + 1);

      if (!isDigit(key)) return '';

      // Date portion should be YYMM format
      // First 2 digits are year, next 2 are month
      const monthPortion = datePortion.slice(2); // Skip YY, get MM part

      if (monthPortion.length === 1) {
        // We have one month digit, check if adding this key makes valid month
        const monthValue = parseInt(monthPortion + key, 10);
        // Valid months are 01-12
        if (monthValue < 1 || monthValue > 12) return '';
        // Valid month, continue
        return 'expMonth';
      }

      // We already have complete YYMM, next digit means we're done
      if (monthPortion.length === 2) return 'end';
      return '';
    }

    // Already at end state
    case 'end':
      return 'end';

    // Empty or unknown segment
    default:
      return '';
  }
};
